import { useEffect, useState } from 'react'

/** Inline margin (px) between an anchored alert and the canvas scroller's edges. */
const EDGE_MARGIN_PX = 12

/**
 * Tracks the live top-right corner of the canvas scroller (`[data-testid="center-canvas-scroller"]`
 * in `center-canvas/center-canvas.tsx`) so an alert can anchor there in viewport (`position: fixed`) coordinates.
 * The scroller's own right edge already sits exactly at the right panel's current left edge in
 * both its open and closed states — `RightPanel` is a real flex sibling, not an overlay — so a
 * `ResizeObserver` on the scroller alone (no right-panel-specific measurement needed) picks up
 * panel open/close, window resize, and left-panel toggling, all in one place.
 */
export function useLiveAreaTopRight(active: boolean): { top: number; right: number } {
  const [pos, setPos] = useState({ top: EDGE_MARGIN_PX, right: EDGE_MARGIN_PX })

  useEffect(() => {
    if (!active) return

    const el = document.querySelector('[data-testid="center-canvas-scroller"]')

    if (!el) return

    const update = () => {
      const rect = el.getBoundingClientRect()
      // The scroller's bounding box includes its own scrollbar track (when content overflows),
      // but the scrollbar isn't part of the visible canvas content — measuring against the
      // outer edge alone lands the alert on top of the scrollbar instead of clearing it. Back
      // off by the scrollbar's width (0 when there isn't one) so both margins read as equal.
      const scrollbarWidth = rect.width - el.clientWidth

      setPos({
        top: rect.top + EDGE_MARGIN_PX,
        right: window.innerWidth - rect.right + scrollbarWidth + EDGE_MARGIN_PX,
      })
    }

    update()

    const resizeObserver = new ResizeObserver(update)

    resizeObserver.observe(el)
    window.addEventListener('resize', update)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [active])

  return pos
}
